import React from 'react';

import { useAuth } from '@components/utilComponents/AuthProvider';

import { createAppTheme } from '../ThemeManager.config';

import type { Theme } from '@mui/material/styles';

/**
 * Режим темы: `forcedThemeMode`, затем настройка пользователя, затем
 * `localStorage`, затем светлая.
 *
 * `forcedThemeMode` перекрывает всё и ничего не пишет в `localStorage` — это
 * отличие analyst от соседей. Прод им не пользуется: единственные потребители
 * — тесты самого `ThemeManager` (проверено поиском, а не комментарием).
 * Убирать его вместе с выносом конфигурации не стал: это уже правка поведения,
 * а не перенос, и место ей в своей задаче.
 *
 * ─── Почему режим ВЫВОДИТСЯ, а не хранится (AF-45) ──────────────────────────
 *
 * Раньше здесь стояло `useState(forcedThemeMode ?? 'light')` плюс эффект,
 * который читал настройки и звал `setThemeMode`. Линтер ругался на
 * присваивание состояния синхронно в эффекте, но лишний рендер — не главная
 * беда.
 *
 * Главная в том, что `useState` тут был **не для состояния, а чтобы дождаться
 * данных**: источником правды всегда оставались настройки пользователя и
 * `localStorage`, а состояние их лишь повторяло с задержкой в один кадр.
 * Отсюда и видимый дефект — первый кадр был светлым, и тёмная тема мелькала.
 *
 * Замер до правки, вычисленный `theme.palette.mode` по кадрам:
 *
 *     настройка dark, storage пуст   кадры: light > dark
 *     только storage dark            кадры: light > dark
 *     forced dark                    кадры: dark          ← мелькания не было
 *
 * Последняя строка показательна: там, где значение приходило сразу через
 * инициализатор, дефекта не возникало. Теперь так работают все случаи.
 *
 * `localStorage` читается один раз ленивым инициализатором. `||`, а не `??`:
 * разница только на пустой строке, и менять её заодно значило бы протащить
 * второе изменение поведения под видом первого.
 */
export const useThemeFromUser = (
  forcedThemeMode?: 'light' | 'dark',
): Theme => {
  const user = useAuth();
  const [storedTheme] = React.useState(() => localStorage.getItem('theme'));

  const themeMode = (forcedThemeMode ||
    user.settings?.theme ||
    storedTheme ||
    'light') as 'light' | 'dark';

  React.useEffect(() => {
    if (!forcedThemeMode && user.settings?.theme) {
      localStorage.setItem('theme', user.settings.theme);
    }
  }, [user.settings?.theme, forcedThemeMode]);

  return React.useMemo(() => createAppTheme(themeMode), [themeMode]);
};
